import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ENV } from '@/constants/env';
import { ONE_MIN_MS } from '@/constants/time';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { useMarketStore } from '@/features/markets/stores/useMarketStore';
import { QueryMarket } from '@/features/markets/types/market';
import { QueryParams } from '@/hooks/usePagination.types';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { AxiosConfig } from '@/models/api';

import { IncidenceStats } from '../../types/incidence-stats';

type GetIncidenceStatsResponse = IncidenceStats;

interface GetIncidenceStatsQueryKeyProps {
    market?: QueryMarket;
    userId?: string;
}

export const getIncidenceStatsQueryKey = ({ market, userId }: GetIncidenceStatsQueryKeyProps = {}) => [
    'getIncidenceStats',
    `market:${market?.id}`,
    `${userId}`,
];

export const getGetIncidenceStatsEndpoint = (searchParams: QueryParams) =>
    buildEndpointUrl('INCIDENCES_TICKETS_STATISTICS', { searchParams }, ENV.API_URL_INCIDENTS);

export const apiGetIncidenceStats = async (searchParams: QueryParams, config?: AxiosConfig) =>
    await HttpClient.get<GetIncidenceStatsResponse>(getGetIncidenceStatsEndpoint(searchParams), {
        envApi: 'INCIDENCE_API',
        ...config,
    });

//

export const useGetIncidenceStats = () => {
    const { authUser, authData } = useAuthStore();
    const { market } = useMarketStore();

    const {
        data,
        isLoading: isInitialLoading,
        isFetching,
    } = useQuery({
        queryKey: getIncidenceStatsQueryKey({ market, userId: authUser?.id }),
        queryFn: ({ signal }) => {
            if (!authUser?.id) return null;

            const queryParams = {
                'agent[]': ['null', authUser.id],
                isInquiry: false,
                isAdminReaded: false,
            } as QueryParams;

            return apiGetIncidenceStats(queryParams, { signal });
        },
        refetchInterval: ONE_MIN_MS,
        enabled: !!authData?.accessToken && !!authUser?.id,
    });

    const incidenceStats = useMemo(() => data, [data]);

    return {
        isLoading: isInitialLoading || isFetching,
        isInitialLoading,
        isFetching,
        incidenceStats,
    };
};
